<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import UserCardList from '../components/access/UserCardList.vue'
import UserDetailPane from '../components/access/UserDetailPane.vue'
import UserFilters from '../components/access/UserFilters.vue'
import UserTable from '../components/access/UserTable.vue'
import { apiErrorMessage, deleteAccessUser, getAccessRoles, getAccessUser, getAccessUsers, updateAccessUserStatus } from '../api'
import { roleCode } from '../session'
import { useI18n } from '../i18n'

const router = useRouter()
const route = useRoute()
const loading = ref(true)
const error = ref('')
const users = ref([])
const roles = ref([])
const total = ref(0)
const selected = ref(null)
const busy = ref(false)
const filters = ref({ search: '', roleId: '', status: '' })
const isAdmin = computed(() => roleCode() === 'ADMIN')
const { t } = useI18n()
const activeCount = computed(() => users.value.filter((user) => user.enabled).length)
const blockedCount = computed(() => users.value.filter((user) => !user.enabled).length)
const assignedStations = computed(() => users.value.reduce((sum, user) => sum + user.stationCount, 0))

async function load() {
  loading.value = true
  error.value = ''
  try {
    const [userPage, rolePage] = await Promise.all([
      getAccessUsers({ ...filters.value, pageSize: 100 }), getAccessRoles({ pageSize: 100 }),
    ])
    users.value = userPage.items
    total.value = userPage.total
    roles.value = rolePage.items
    if (selected.value) {
      selected.value = (await getAccessUser(selected.value.id)).user
    }
  } catch (cause) {
    error.value = apiErrorMessage(t, cause)
  } finally {
    loading.value = false
  }
}

async function selectUser(user) {
  if (String(route.params.userId || '') !== String(user.id)) {
    await router.push({ name: 'access-user-detail', params: { userId: user.id } })
    return
  }
  error.value = ''
  try {
    selected.value = (await getAccessUser(user.id)).user
  } catch (cause) {
    error.value = apiErrorMessage(t, cause)
  }
}

async function syncSelectedFromRoute() {
  const userId = Number(route.params.userId)
  if (!Number.isInteger(userId) || userId < 1) {
    selected.value = null
    return
  }
  await selectUser({ id: userId })
}

async function closeDetail() {
  const returnUserId = selected.value?.id
  await router.push({ name: 'access-users' })
  selected.value = null
  await nextTick()
  const candidates = returnUserId
    ? [...document.querySelectorAll(`[data-user-id="${returnUserId}"]`)]
    : []
  candidates.find((candidate) => candidate.getClientRects().length)?.focus()
}

function resetFilters() {
  filters.value = { search: '', roleId: '', status: '' }
  load()
}

async function toggleUser(user) {
  if (!window.confirm(t(user.enabled ? 'blockUserAccessConfirm' : 'unblockUserAccessConfirm', { name: user.displayName }))) return
  busy.value = true
  error.value = ''
  try {
    selected.value = (await updateAccessUserStatus(user.id, !user.enabled, user.version)).user
    await load()
  } catch (cause) {
    error.value = cause.code === 'version_conflict' ? t('userVersionConflict') : apiErrorMessage(t, cause)
    if (cause.code === 'version_conflict') await selectUser(user)
  } finally {
    busy.value = false
  }
}

async function removeUser(user) {
  if (!window.confirm(t('deleteAccountConfirm', { name: user.displayName }))) return
  busy.value = true
  error.value = ''
  try {
    await deleteAccessUser(user.id, user.version)
    selected.value = null
    await router.push({ name: 'access-users' })
    await load()
  } catch (cause) {
    error.value = apiErrorMessage(t, cause)
  } finally {
    busy.value = false
  }
}

watch(() => route.params.userId, syncSelectedFromRoute)
onMounted(async () => {
  await load()
  await syncSelectedFromRoute()
})
</script>

<template>
  <section class="access-page" :class="{ 'has-detail': selected }">
    <div class="access-main">
      <header class="access-page-header"><div><p class="eyebrow">{{ t('accessManagement') }}</p><h1>{{ t('users') }}</h1><p>{{ t('accessUsersIntro') }}</p></div>
        <button v-if="isAdmin" class="button access-primary" type="button" @click="router.push({ name: 'access-user-new' })">{{ t('addUser') }}</button></header>
      <p v-if="!isAdmin" class="notice info">{{ t('accessReadOnly') }}</p>
      <p v-if="error" class="notice error" role="alert">{{ error }}</p>
      <div class="access-summary-grid">
        <article><span>{{ t('allUsers') }}</span><strong>{{ total }}</strong></article>
        <article><span>{{ t('activeUsers') }}</span><strong>{{ activeCount }}</strong></article>
        <article><span>{{ t('blockedUsers') }}</span><strong>{{ blockedCount }}</strong></article>
        <article><span>{{ t('stationAssignments') }}</span><strong>{{ assignedStations }}</strong></article>
      </div>
      <section class="access-list-card">
        <UserFilters v-model:search="filters.search" v-model:role-id="filters.roleId" v-model:status="filters.status"
          :roles="roles" @apply="load" @reset="resetFilters" />
        <p v-if="loading" class="access-loading">{{ t('loadingUsers') }}</p>
        <div v-else-if="!users.length" class="access-empty"><h2>{{ t('usersNotFound') }}</h2><p>{{ t('usersNotFoundHint') }}</p></div>
        <template v-else>
          <UserTable :users="users" :selected-id="selected?.id" @select="selectUser" />
          <UserCardList :users="users" :selected-id="selected?.id" @select="selectUser" />
        </template>
      </section>
    </div>
    <button v-if="selected" class="access-detail-backdrop" type="button" :aria-label="t('close')" @click="closeDetail"></button>
    <UserDetailPane :user="selected" :busy="busy" @close="closeDetail" @toggle="toggleUser" @remove="removeUser" />
  </section>
</template>
